import React, { useState, useEffect, useRef } from 'react';
import {
  AppBar, Toolbar, Typography, Button, Box, Badge, IconButton, Tooltip,
  Popover, List, ListItemButton, ListItemText, Divider,
  Dialog, DialogTitle, DialogContent, DialogActions,
  FormGroup, FormControlLabel, Checkbox,
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  PlayCircleOutline as EnCoursIcon,
  People as ElevesIcon,
  Build as WorkshopIcon,
  Search as SearchIcon,
  Message as MessageIcon,
  AccountCircle as AccountIcon,
  Logout as LogoutIcon,
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  History as HistoryIcon,
  DeleteSweep as DeleteSweepIcon,
  DarkMode as DarkModeIcon,
  LightMode as LightModeIcon,
  CachedOutlined as CacheIcon,
  Videocam as CameraIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useAuthStore } from '../../store/authStore';
import { useThemeStore } from '../../store/themeStore';
import { useNavigationHistoryStore } from '../../store/navigationHistoryStore';
import { useNotificationStore } from '../../store/notificationStore';
import teacherMessageService from '../../services/teacherMessage.service';
import api from '../../services/api';

const Navbar: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuthStore();
  const { mode, toggleMode } = useThemeStore();
  const [unreadCount, setUnreadCount] = useState(0);
  const [historyAnchor, setHistoryAnchor] = useState<HTMLElement | null>(null);
  const [cacheDialogOpen, setCacheDialogOpen] = useState(false);
  const [cacheSelections, setCacheSelections] = useState<Record<string, boolean>>({
    pattern_containers: false,
    patterns: false,
    courses: false,
    all: false,
  });
  const [clearingCache, setClearingCache] = useState(false);
  const historyEntries = useNavigationHistoryStore((s) => s.entries);
  const pushHistory = useNavigationHistoryStore((s) => s.push);
  const clearHistory = useNavigationHistoryStore((s) => s.clear);
  const loadHistory = useNavigationHistoryStore((s) => s.load);
  const historyLoaded = useNavigationHistoryStore((s) => s.loaded);
  const listEndRef = useRef<HTMLDivElement>(null);

  // Load history from backend on mount
  useEffect(() => {
    if (!historyLoaded) loadHistory();
  }, [historyLoaded, loadHistory]);

  // Track page-level navigation in history
  const prevPathRef = useRef(location.pathname + location.search);
  useEffect(() => {
    const current = location.pathname + location.search;
    if (current !== prevPathRef.current) {
      prevPathRef.current = current;
      // Only push non-workshop pages (workshop handles its own history)
      if (!location.pathname.startsWith('/workshop')) {
        const pageLabels: Record<string, string> = {
          '/': 'En cours',
          '/eleves': 'Élèves',
          '/search': 'Recherche',
          '/messages': 'Messages',
          '/account': 'Mon compte',
          '/camera': 'Caméra',
        };
        const label = pageLabels[location.pathname] || location.pathname;
        pushHistory(current, label);
      }
    }
  }, [location, pushHistory]);

  // Scroll history list to bottom when opened
  useEffect(() => {
    if (historyAnchor && listEndRef.current) {
      setTimeout(() => listEndRef.current?.scrollIntoView({ block: 'end' }), 50);
    }
  }, [historyAnchor]);

  // Fetch unread message count
  const fetchUnreadCount = async () => {
    try {
      const count = await teacherMessageService.getUnreadCount();
      setUnreadCount(count);
    } catch (error) {
      console.error('Error fetching unread count:', error);
    }
  };

  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Refresh badge instantly when a chat notification arrives via WebSocket
  const wsNotifications = useNotificationStore((s) => s.notifications);
  useEffect(() => {
    const lastNotif = wsNotifications[0];
    if (lastNotif && (lastNotif.type === 'chat_message' || lastNotif.category === 'chat')) {
      fetchUnreadCount();
    }
  }, [wsNotifications]); // eslint-disable-line react-hooks/exhaustive-deps

  // Refresh badge when messages are read
  useEffect(() => {
    const handler = () => fetchUnreadCount();
    window.addEventListener('messages-read', handler);
    return () => window.removeEventListener('messages-read', handler);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleHistoryClick = (url: string) => {
    setHistoryAnchor(null);
    navigate(url);
  };

  const formatTime = (timestamp: number) => {
    const d = new Date(timestamp);
    return d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  };

  const cacheCategories: { id: string; label: string }[] = [
    { id: 'pattern_containers', label: 'Containers de patterns' },
    { id: 'patterns', label: 'Patterns' },
    { id: 'courses', label: 'Cours' },
    { id: 'all', label: 'Tout le cache' },
  ];

  const handleCacheToggle = (id: string) => {
    if (id === 'all') {
      const newVal = !cacheSelections.all;
      setCacheSelections({
        pattern_containers: newVal,
        patterns: newVal,
        courses: newVal,
        all: newVal,
      });
    } else {
      setCacheSelections((prev) => {
        const updated = { ...prev, [id]: !prev[id] };
        // Uncheck "all" if any individual is unchecked
        if (!updated[id]) updated.all = false;
        // Check "all" if all individuals are checked
        if (updated.pattern_containers && updated.patterns && updated.courses) updated.all = true;
        return updated;
      });
    }
  };

  const handleClearCache = async () => {
    const selected = Object.entries(cacheSelections)
      .filter(([, v]) => v)
      .map(([k]) => k);
    if (selected.length === 0) return;

    setClearingCache(true);
    try {
      // If "all" is selected, just send ["all"]
      const categories = selected.includes('all') ? ['all'] : selected;
      await api.post('/cache/clear/', { categories });
      toast.success('Cache vidé avec succès');
      setCacheDialogOpen(false);
      setCacheSelections({ pattern_containers: false, patterns: false, courses: false, all: false });
    } catch {
      toast.error('Erreur lors du vidage du cache');
    } finally {
      setClearingCache(false);
    }
  };

  const navItems = [
    { label: 'En cours', path: '/', icon: <EnCoursIcon /> },
    { label: 'Élèves', path: '/eleves', icon: <ElevesIcon /> },
    { label: 'Atelier', path: '/workshop', icon: <WorkshopIcon /> },
    { label: 'Recherche', path: '/search', icon: <SearchIcon /> },
    { label: 'Messages', path: '/messages', icon: <Badge badgeContent={unreadCount} color="error"><MessageIcon /></Badge> },
    { label: 'Caméra', path: '/camera', icon: <CameraIcon /> },
    { label: 'Mon compte', path: '/account', icon: <AccountIcon /> },
  ];

  const currentUrl = location.pathname + location.search;

  return (
    <AppBar position="static">
      <Toolbar>
        <Box sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>
          <Tooltip title="Précédent">
            <IconButton color="inherit" onClick={() => navigate(-1)} size="small">
              <ArrowBackIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Suivant">
            <IconButton color="inherit" onClick={() => navigate(1)} size="small">
              <ArrowForwardIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Historique">
            <IconButton
              color="inherit"
              onClick={(e) => setHistoryAnchor(e.currentTarget)}
              size="small"
            >
              <Badge badgeContent={historyEntries.length} color="secondary" max={99}>
                <HistoryIcon />
              </Badge>
            </IconButton>
          </Tooltip>
        </Box>

        <Popover
          open={Boolean(historyAnchor)}
          anchorEl={historyAnchor}
          onClose={() => setHistoryAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          slotProps={{
            paper: {
              sx: { maxHeight: 480, width: 360, overflow: 'auto' },
            },
          }}
        >
          <Box sx={{ px: 2, py: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="subtitle2" color="text.secondary">
              Historique de navigation
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="caption" color="text.secondary">
                {historyEntries.length} entrée{historyEntries.length > 1 ? 's' : ''}
              </Typography>
              {historyEntries.length > 0 && (
                <Tooltip title="Effacer l'historique">
                  <IconButton
                    size="small"
                    onClick={() => { clearHistory(); setHistoryAnchor(null); }}
                    sx={{ color: 'text.secondary' }}
                  >
                    <DeleteSweepIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          </Box>
          <Divider />
          {historyEntries.length === 0 ? (
            <Box sx={{ p: 2 }}>
              <Typography variant="body2" color="text.secondary" align="center">
                Aucun historique
              </Typography>
            </Box>
          ) : (
            <List dense disablePadding>
              {[...historyEntries].reverse().map((entry, idx) => {
                const isActive = entry.url === currentUrl;
                return (
                  <ListItemButton
                    key={`${entry.timestamp}-${idx}`}
                    onClick={() => handleHistoryClick(entry.url)}
                    selected={isActive}
                    sx={{
                      borderLeft: isActive ? '3px solid' : '3px solid transparent',
                      borderColor: isActive ? 'primary.main' : 'transparent',
                    }}
                  >
                    <ListItemText
                      primary={entry.label}
                      secondary={formatTime(entry.timestamp)}
                      primaryTypographyProps={{
                        variant: 'body2',
                        fontWeight: isActive ? 600 : 400,
                        noWrap: true,
                      }}
                      secondaryTypographyProps={{ variant: 'caption' }}
                    />
                  </ListItemButton>
                );
              })}
              <div ref={listEndRef} />
            </List>
          )}
        </Popover>

        <Box sx={{ flexGrow: 1, display: 'flex', gap: 1 }}>
          {navItems.map((item) => (
            <Button
              key={item.path}
              color="inherit"
              startIcon={item.icon}
              onClick={() => navigate(item.path)}
              sx={{
                backgroundColor: location.pathname === item.path ? 'rgba(255,255,255,0.1)' : 'transparent',
                '&:hover': {
                  backgroundColor: 'rgba(255,255,255,0.2)',
                },
              }}
            >
              {item.label}
            </Button>
          ))}
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="body2">
            {user?.first_name} {user?.last_name}
          </Typography>
          <Tooltip title="Vider le cache">
            <IconButton color="inherit" onClick={() => setCacheDialogOpen(true)} size="small">
              <CacheIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title={mode === 'light' ? 'Mode sombre' : 'Mode clair'}>
            <IconButton color="inherit" onClick={toggleMode} size="small">
              {mode === 'light' ? <DarkModeIcon /> : <LightModeIcon />}
            </IconButton>
          </Tooltip>
          <Button
            color="inherit"
            startIcon={<LogoutIcon />}
            onClick={handleLogout}
            size="small"
          >
            Déconnexion
          </Button>
        </Box>
      </Toolbar>

      <Dialog open={cacheDialogOpen} onClose={() => setCacheDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Vider le cache</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Sélectionnez les catégories de cache à vider :
          </Typography>
          <FormGroup>
            {cacheCategories.map((cat) => (
              <FormControlLabel
                key={cat.id}
                control={
                  <Checkbox
                    checked={cacheSelections[cat.id] || false}
                    onChange={() => handleCacheToggle(cat.id)}
                  />
                }
                label={cat.label}
                sx={cat.id === 'all' ? { mt: 1, borderTop: '1px solid', borderColor: 'divider', pt: 1 } : {}}
              />
            ))}
          </FormGroup>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCacheDialogOpen(false)}>Annuler</Button>
          <Button
            onClick={handleClearCache}
            variant="contained"
            color="warning"
            disabled={clearingCache || !Object.values(cacheSelections).some(Boolean)}
          >
            {clearingCache ? 'Vidage...' : 'Vider'}
          </Button>
        </DialogActions>
      </Dialog>
    </AppBar>
  );
};

export default Navbar;
